import type { IDependent } from '../../../hooks/queries/useDependents';

/**
 * Display name for a dependent in user-facing copy. Falls back to
 * `firstName` when no `preferredName` is set — same precedence as the
 * dependents pane elsewhere in the app.
 */
export const getDisplayName = (dependent: IDependent): string => dependent.preferredName?.trim() || dependent.firstName;

/**
 * Render `["Jane", "Peter"]` as `"Jane and Peter"` and `["A", "B", "C"]`
 * as `"A, B, and C"` (Oxford comma). Pass `emptyFallback` to control
 * the 0-length case — the entry step renders the bare title without
 * a fallback while the review step substitutes a pronoun, so each
 * caller chooses what makes grammatical sense for its sentence.
 */
export const joinNames = (names: ReadonlyArray<string>, emptyFallback: string = ''): string => {
  if (names.length === 0) {
    return emptyFallback;
  }
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`;
  }
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

/**
 * Joined display names for user-facing copy — `"Jane and Peter"`. Empty string
 * when the list is empty, so callers can branch to a generic fallback.
 */
export const getDependentNames = (dependents: ReadonlyArray<IDependent>): string =>
  joinNames(dependents.map(getDisplayName));

/**
 * Possessive form of the joined display names — `"Jane and Peter's"`. The `'s`
 * is appended only to the names branch, so `fallback` must carry its own
 * apostrophe-s (`"your loved one's"`, not `"your loved one"`); it's returned
 * verbatim when no names have resolved yet, so a cold render never shows a bare
 * apostrophe-s.
 */
export const getDependentPossessive = (dependents: ReadonlyArray<IDependent>, fallback: string): string => {
  const names = getDependentNames(dependents);
  return names ? `${names}'s` : fallback;
};
